import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Menu, X } from "lucide-react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_med-transcribe-4/artifacts/hi10en9r_ChatGPT%20Image%20Feb%208%2C%202026%2C%2008_43_59%20PM.png";

const Header = ({ onScheduleDemo, onContactUs }) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const isHomePage = location.pathname === "/";

  const navLinks = [
    { label: "Services", href: "#services" },
    { label: "Workflow", href: "#workflow" },
    { label: "About", href: "#about" },
  ];

  const handleNavClick = (href) => {
    setIsMobileMenuOpen(false);
    if (!isHomePage) {
      navigate("/", { state: { scrollTo: href } });
    } else {
      const element = document.querySelector(href);
      if (element) {
        element.scrollIntoView({ behavior: "smooth" });
      }
    }
  };

  return (
    <header 
      data-testid="header"
      className="fixed top-0 left-0 right-0 z-50 bg-[#0A0A0A]/80 backdrop-blur-xl border-b border-white/5"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link 
            to="/" 
            className="flex items-center gap-3"
            data-testid="header-logo"
          >
            <img 
              src={LOGO_URL} 
              alt="MedinoteX Logo" 
              className="h-10 w-auto"
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <button
                key={link.label}
                onClick={() => handleNavClick(link.href)}
                className="text-slate-400 hover:text-cyan-400 transition-colors text-sm font-medium"
                data-testid={`nav-${link.label.toLowerCase()}`}
              >
                {link.label}
              </button>
            ))}
          </nav>

          {/* Desktop CTAs */}
          <div className="hidden md:flex items-center gap-4">
            <Button
              variant="ghost"
              onClick={onContactUs}
              data-testid="header-contact-btn"
              className="text-slate-300 hover:text-white hover:bg-white/5"
            >
              Contact Us
            </Button>
            <Button
              onClick={onScheduleDemo}
              data-testid="header-demo-btn"
              className="bg-cyan-500 hover:bg-cyan-400 text-black font-semibold rounded-full px-6 shadow-[0_0_20px_rgba(6,182,212,0.3)] hover:shadow-[0_0_30px_rgba(6,182,212,0.5)]"
            >
              Schedule Demo
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="md:hidden p-2 text-slate-400 hover:text-white"
            data-testid="mobile-menu-btn"
          >
            {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="md:hidden mobile-menu">
          <div className="pt-24 px-6 pb-8">
            <nav className="flex flex-col gap-6">
              {navLinks.map((link) => (
                <button
                  key={link.label}
                  onClick={() => handleNavClick(link.href)}
                  className="text-xl text-slate-300 hover:text-cyan-400 transition-colors text-left"
                >
                  {link.label}
                </button>
              ))}
            </nav>
            <div className="mt-8 flex flex-col gap-4">
              <Button
                variant="outline"
                onClick={() => {
                  setIsMobileMenuOpen(false);
                  onContactUs();
                }}
                className="w-full h-12 border-white/10 text-white hover:bg-white/5"
              >
                Contact Us
              </Button>
              <Button
                onClick={() => {
                  setIsMobileMenuOpen(false);
                  onScheduleDemo();
                }}
                className="w-full h-12 bg-cyan-500 hover:bg-cyan-400 text-black font-semibold rounded-full"
              >
                Schedule Demo
              </Button>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
